import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Star, CreditCard, ArrowDown, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { detectGeo } from "@/lib/geo";
import { CustomPlanDialog } from "@/components/CustomPlanDialog";

const ORDER = { free: 0, standard: 1, premium: 2 };

// Razorpay hosted subscription links (India)
const RAZORPAY_LINKS = {
  standard: "https://rzp.io/rzp/zMTL1hD",
  premium: "https://rzp.io/rzp/4MODTiTd",
};

// Stripe hosted payment links (international / USD)
const STRIPE_LINKS = {
  standard: "https://buy.stripe.com/bJe7sK7ri0Am19K6ewc7u1w",
  premium: "https://buy.stripe.com/fZu6oG7rierc4lW9qIc7u1x",
};

export default function MyListing() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [geo, setGeo] = useState({ currency: "USD", gateway: "stripe", symbol: "US$" });
  const [payPlan, setPayPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  const load = () => api.get("/institution/listing").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { detectGeo().then(setGeo).catch(() => {}); }, []);
  if (!data) return <div className="h-64 animate-pulse rounded-xl bg-slate-200" />;

  const isIndia = geo.currency === "INR";

  const current = data.current_plan;
  const plans = data.plans || [];

  const startStripe = (plan) => {
    const link = STRIPE_LINKS[plan];
    if (!link) { toast.error("Payment link not available for this plan"); return; }
    window.open(link, "_blank", "noopener");
    setPayPlan(null);
    toast.message("Complete your payment in the Stripe tab. Your plan updates once payment is confirmed.");
  };

  const startRazorpay = (plan) => {
    const link = RAZORPAY_LINKS[plan];
    if (!link) { toast.error("Payment link not available for this plan"); return; }
    window.open(link, "_blank", "noopener");
    setPayPlan(null);
    toast.message("Complete your subscription in the Razorpay tab. Your plan updates once payment is confirmed.");
  };

  const downgrade = async (target) => {
    if (!window.confirm(`Downgrade to ${target}?`)) return;
    try {
      const { data: res } = await api.post("/institution/listing/downgrade", { target_plan: target });
      toast.success(target === "free" ? "Scheduled downgrade to Free" : `Standard will activate on ${(res.effective || "").slice(0, 10)}`);
      await refresh(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="mb-1 font-heading text-2xl font-bold tracking-tight text-slate-900">My Listing</h1>
      <p className="mb-6 text-sm text-slate-500">Manage your listing plan and visibility</p>

      <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-5">
        <p className="text-sm text-slate-600">Current Plan</p>
        <p className="font-heading text-2xl font-bold capitalize text-slate-900" data-testid="current-plan">{current}</p>
        {data.subscription_expiry && <p className="text-sm text-slate-500">Valid until {data.subscription_expiry.slice(0, 10)}</p>}
        {data.pending_downgrade_plan && <p className="mt-1 text-sm text-amber-700">Scheduled downgrade to {data.pending_downgrade_plan} at period end.</p>}
      </div>

      <div className="grid gap-5 md:grid-cols-3">
        {plans.map((p) => {
          const isCurrent = p.name === current;
          const isUpgrade = ORDER[p.name] > ORDER[current];
          return (
            <div key={p.id} data-testid={`listing-plan-${p.name}`} className={`flex flex-col rounded-xl border bg-white p-5 ${
              p.name === "premium" ? "border-sky-300" : "border-slate-200"}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold capitalize text-slate-900">{p.name}</h3>
                {p.name === "premium" && <Star className="h-5 w-5 fill-sky-500 text-sky-500" />}
              </div>
              <p className="mt-2 font-heading text-2xl font-bold text-slate-900">
                {p.name === "free" ? "Free" : (isIndia
                  ? `₹${p.price_inr.toLocaleString("en-IN")}`
                  : `US$ ${p.price_usd.toLocaleString("en-US")}`)}
                {p.name !== "free" && <span className="text-sm font-normal text-slate-400">/yr</span>}
              </p>
              {p.name !== "free" && <p className="text-xs text-slate-400">per year</p>}
              <ul className="mt-4 flex-1 space-y-1.5">
                {(p.features || []).slice(0, 5).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-800" />{f}</li>
                ))}
              </ul>
              <div className="mt-5">
                {isCurrent ? (
                  <Button disabled variant="outline" className="w-full">Current Plan</Button>
                ) : isUpgrade ? (
                  <Button className="w-full bg-blue-800 hover:bg-blue-900" onClick={() => setPayPlan(p)} data-testid={`upgrade-${p.name}`}>
                    Upgrade to {p.name}
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full" onClick={() => downgrade(p.name)} data-testid={`downgrade-${p.name}`}>
                    <ArrowDown className="mr-1 h-4 w-4" /> Downgrade
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-col items-start justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50/50 p-5 sm:flex-row sm:items-center">
        <div>
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Sparkles className="h-5 w-5 text-amber-600" /> Custom Listing Plan</h3>
          <p className="text-sm text-slate-500">Listing more than 50 courses? Request a tailored plan for higher volumes.</p>
        </div>
        <Button className="bg-amber-500 text-white hover:bg-amber-600" onClick={() => setCustomOpen(true)} data-testid="request-custom-plan-btn">
          Request Custom Plan
        </Button>
      </div>

      <CustomPlanDialog open={customOpen} onOpenChange={setCustomOpen}
        defaultName={data.contact_person || ""} institutionName={current ? (data.name || "") : ""} />

      <Dialog open={!!payPlan} onOpenChange={(o) => !o && setPayPlan(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Upgrade to {payPlan?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Annual listing. Secure payment.</p>
          <div className="mt-2 space-y-3">
            {isIndia ? (
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-sm text-slate-500">India (₹)</p>
                <p className="font-heading text-xl font-bold text-slate-900">₹{payPlan?.price_inr?.toLocaleString("en-IN")}</p>
                <Button className="mt-2 w-full bg-blue-800 hover:bg-blue-900" disabled={busy} onClick={() => startRazorpay(payPlan.name)} data-testid="pay-razorpay-btn">
                  <CreditCard className="mr-2 h-4 w-4" /> Subscribe with Razorpay
                </Button>
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-sm text-slate-500">International ($)</p>
                <p className="font-heading text-xl font-bold text-slate-900">US$ {payPlan?.price_usd?.toLocaleString("en-US")}</p>
                <Button className="mt-2 w-full bg-blue-800 hover:bg-blue-900" disabled={busy} onClick={() => startStripe(payPlan.name)} data-testid="pay-stripe-btn">
                  <CreditCard className="mr-2 h-4 w-4" /> Subscribe with Stripe
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
